import React, { useState, useEffect } from 'react';
import { useAdminStore } from '../store/useAdminStore';

function AdminDevOps() {
  const [isRestarting, setIsRestarting] = useState(false);
  const [isFlushing, setIsFlushing] = useState(false);
  const [schema, setSchema] = useState([]);
  const { adminIdempotencyKey, regenerateAdminIdempotencyKey, deviceId } = useAdminStore();

  useEffect(() => {
    fetch('/api/admin/devops/schema', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(r => r.json())
      .then(data => {
        // Group by table_name
        const grouped = (Array.isArray(data) ? data : []).reduce((acc, curr) => {
          if (!acc[curr.table_name]) acc[curr.table_name] = [];
          acc[curr.table_name].push(curr);
          return acc;
        }, {});
        setSchema(grouped);
      })
      .catch(console.error);
  }, []);

  const handleRestart = async () => {
    if (!window.confirm("⚠️ DANGER: Are you sure you want to hard restart the Node.js server on Render? Expect 10-15 seconds of downtime.")) return;
    
    setIsRestarting(true);
    try {
      const res = await fetch('/api/admin/devops/restart', { method: 'POST', headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      if (res.ok && data.success) {
        alert("Restart signal sent successfully. Server is rebooting.");
      } else {
        alert("Failed to restart: " + (data.error || "Unknown error"));
      }
    } catch {
      alert("Network error communicating with DevOps API.");
    } finally {
      setIsRestarting(false);
      regenerateAdminIdempotencyKey();
    }
  };

  const handleFlushRedis = async () => {
    if (!window.confirm("Are you sure you want to clear the entire Redis Cache? All cached queries and rate-limits will be wiped.")) return;
    
    setIsFlushing(true);
    try {
      const res = await fetch('/api/admin/devops/flush-redis', { method: 'POST', headers: { 'Idempotency-Key': adminIdempotencyKey, 'X-Device-Id': deviceId || '', 'X-Requested-With': 'XMLHttpRequest' } });
      const data = await res.json();
      if (res.ok && data.success) {
        alert("Redis Cache cleared successfully.");
      } else {
        alert("Failed to clear Redis.");
      }
    } catch {
      alert("Network error.");
    } finally {
      setIsFlushing(false);
      regenerateAdminIdempotencyKey();
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-white tracking-tight flex items-center gap-3">
          <span>🛠️</span> DevOps & Infrastructure
        </h1>
        <p className="text-gray-400 text-sm mt-1">Control your physical server, clear caches, and manage deployments.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* RENDER CONTROLS */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>☁️</span> Render Platform Controls
          </h2>
          <p className="text-gray-400 text-sm mb-6">Direct integration with the Render.com API. Hard-reboot the instance without logging into the Render dashboard.</p>
          
          <button 
            onClick={handleRestart}
            disabled={isRestarting}
            className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-bold py-4 rounded-lg shadow-[0_0_20px_rgba(220,38,38,0.4)] transition-all flex justify-center items-center gap-2">
            {isRestarting ? 'Sending Kill Signal...' : 'Restart Node.js Server'}
          </button>
          <p className="text-xs text-center text-gray-500 mt-3 font-mono">POST /v1/services/&#123;service_id&#125;/restart</p>
        </div>

        {/* REDIS CONTROLS */}
        <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <span>⚡</span> Upstash Redis Controls
          </h2>
          <p className="text-gray-400 text-sm mb-6">Instantly invalidate all cached API responses, rate limiters, and session data. Use this if the UI is showing stale data.</p>
          
          <button 
            onClick={handleFlushRedis}
            disabled={isFlushing}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white font-bold py-4 rounded-lg shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all flex justify-center items-center gap-2">
            {isFlushing ? 'Executing FLUSHALL...' : 'Clear Global Cache (FLUSHALL)'}
          </button>
        </div>
      </div>

      {/* DATABASE SCHEMA VISUALIZER */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl shadow-lg p-6 mt-8">
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <span>🗄️</span> Database Schema Visualizer
        </h2>
        <p className="text-gray-400 text-sm mb-6">Auto-generated schema visualization of your PostgreSQL architecture.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(schema).map(([tableName, columns]) => (
            <div key={tableName} className="bg-gray-950 border border-gray-800 rounded-lg overflow-hidden">
              <div className="bg-blue-900/20 border-b border-gray-800 p-3">
                <h3 className="text-blue-400 font-bold font-mono text-sm">{tableName}</h3>
              </div>
              <ul className="p-3 space-y-2">
                {columns.map(col => (
                  <li key={col.column_name} className="flex justify-between text-xs font-mono">
                    <span className="text-gray-300">{col.column_name}</span>
                    <span className="text-purple-400">{col.data_type}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AdminDevOps;
